import { Phone } from "./components/mobile/phone-shell";
import { TodayScreen } from "./sections/mobile-screens/today-screen";
import { SkillsScreen } from "./sections/mobile-screens/skills-screen";
import { InsightScreen } from "./sections/mobile-screens/insight-screen";
import { ChatScreen } from "./sections/mobile-screens/chat-screen";
import { MeScreen } from "./sections/mobile-screens/me-screen";
import { AnalysisRunScreen } from "./sections/mobile-screens/analysis-screen";
import { TrackerLogsScreen } from "./sections/mobile-screens/tracker-logs-screen";
import { CaptureScreen } from "./sections/mobile-screens/capture-screen";
import { Principle } from "./sections/mobile-journey/journey-shared";
import { JourneyDiagram } from "./sections/mobile-journey/journey-diagram";

// ─── Main page ────────────────────────────────────────────────────────────────

export default function MobilePage() {
  const screens = [
    {
      id: "today",
      active: "home",
      label: "01 — Today",
      content: <TodayScreen />,
      notes: [
        "Primary signal first: greeting, insight, trackers, then utility actions.",
        "Tracker cards use coded badges and muted states to cut visual noise.",
        "Utility actions stay neutral so health context remains the focal layer.",
      ],
    },
    {
      id: "skills",
      active: "skills",
      label: "02 — Skills",
      content: <SkillsScreen />,
      notes: [
        "One clear hierarchy: filters, data-gap callout, then author collections.",
        "Each author can present multiple skills in one place, with identity and catalog together.",
        "View and Chat remain compact per skill, so multi-skill browsing stays lightweight.",
      ],
    },
    {
      id: "insight",
      active: "skills",
      label: "03 — Insight",
      content: <InsightScreen />,
      scrollable: true,
      notes: [
        "Evidence and recommendations live in one scroll so interpretation and action stay linked.",
        "Research references are compact metadata, not heavy editorial cards.",
        "Calls to action are tinted, not loud, to preserve analytical calm.",
      ],
    },
    {
      id: "chat",
      active: "chat",
      label: "04 — Chat",
      content: <ChatScreen />,
      notes: [
        "Context chips ground the conversation in live biomarker state.",
        "Assistant responses are softly tinted; user messages stay neutral.",
        "Inline visual evidence prevents tab switching and supports trust.",
      ],
    },
    {
      id: "me",
      active: "profile",
      label: "05 — Me",
      content: <MeScreen />,
      scrollable: true,
      notes: [
        "Profile context first, then structured health data, then raw library.",
        "Every section is a compact summary that drills into a detail screen.",
        "Health data uses color-coded category tiles; the library stays neutral.",
      ],
    },
    {
      id: "analysis-run",
      active: "skills",
      label: "06 — Analysis Run",
      content: <AnalysisRunScreen />,
      notes: [
        "Progress is shown as a single quiet timeline with explicit states.",
        "Only the active node gets accent emphasis.",
        "System behavior is legible without technical jargon.",
      ],
    },
    {
      id: "tracker-logs",
      active: "home",
      label: "07 — Tracker Logs",
      content: <TrackerLogsScreen />,
      scrollable: true,
      notes: [
        "Week pattern first, summary metrics second, entries last.",
        "Streak feedback is textual and minimal.",
        "Logs preserve both quantitative signal and personal context.",
      ],
    },
    {
      id: "capture",
      active: "home",
      label: "08 — Capture",
      content: <CaptureScreen />,
      notes: [
        "One sheet, five modes: note, voice, photo, file, scan.",
        "Serif placeholder invites narrative capture, not file uploads.",
        "Contextual suggestions surface active trackers and skill data needs.",
      ],
    },
  ];

  return (
    <div
      className="min-h-screen"
      style={{
        background:
          "radial-gradient(ellipse at 15% 10%, rgba(255,208,40,0.08) 0%, transparent 50%), radial-gradient(ellipse at 85% 90%, rgba(91,122,102,0.07) 0%, transparent 50%), linear-gradient(165deg, #F7F4ED 0%, var(--color-kin-white) 40%, #F2F4EF 70%, #EDF1EC 100%)",
        fontFamily: "'Manrope', system-ui",
      }}
    >
      {/* Top nav */}
      <nav
        className="sticky top-0 z-40 flex items-center gap-4 px-6 sm:px-10 py-3"
        style={{
          background: "rgba(247,245,238,0.70)",
          backdropFilter: "blur(20px) saturate(120%)",
          borderBottom: "1px solid rgba(255,255,255,0.64)",
        }}
      >
        <a
          href="/private/design"
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: "var(--color-kin-muted)",
            textDecoration: "none",
          }}
        >
          ← Brandbook
        </a>
        <span style={{ color: "var(--color-kin-olive-light)" }}>·</span>
        <p
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: "var(--color-kin-black)",
          }}
        >
          Mobile Screens
        </p>
      </nav>

      {/* Hero */}
      <header className="px-6 sm:px-10 lg:px-16 pt-14 pb-12 max-w-5xl">
        <p
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.24em",
            textTransform: "uppercase",
            color: "var(--color-kin-muted)",
            marginBottom: 14,
          }}
        >
          07 — Mobile Screen Design
        </p>
        <h1
          style={{
            fontSize: "clamp(40px, 6vw, 68px)",
            fontWeight: 700,
            color: "var(--color-kin-black)",
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            lineHeight: 1.0,
            letterSpacing: "-0.01em",
            marginBottom: 20,
          }}
        >
          Kin Mobile —<br />
          <span style={{ color: "var(--color-kin-green-soft)" }}>Calmtech Direction</span>
        </h1>
        <p
          style={{
            fontSize: 17,
            color: "var(--color-kin-muted)",
            lineHeight: 1.7,
            maxWidth: 560,
            fontWeight: 400,
          }}
        >
          A quieter visual system for the same product behavior. Fewer saturated surfaces, tighter
          hierarchy, and translucent navigation that feels stable across screens.
        </p>
      </header>

      {/* ─── User Journey ─────────────────────────────────────────────── */}
      <JourneyDiagram />

      {/* Screens grid — 2 rows × 3 columns */}
      <section className="px-6 sm:px-10 lg:px-16 pb-16">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, 270px)",
            gap: "48px 40px",
            paddingBottom: 12,
          }}
        >
          {screens.map((s) => (
            <Phone
              key={s.id}
              active={s.active}
              label={s.label}
              notes={s.notes}
              scrollable={s.scrollable}
            >
              {s.content}
            </Phone>
          ))}
        </div>
      </section>

      {/* Design principles */}
      <section
        className="px-6 sm:px-10 lg:px-16 py-16"
        style={{ borderTop: "1px solid rgba(108,108,112,0.10)" }}
      >
        <div className="max-w-6xl mx-auto">
          <p
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.24em",
              textTransform: "uppercase",
              color: "var(--color-kin-muted)",
              marginBottom: 14,
            }}
          >
            Design Principles
          </p>
          <h2
            style={{
              fontSize: "clamp(28px, 4vw, 44px)",
              fontWeight: 700,
              color: "var(--color-kin-black)",
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              lineHeight: 1.1,
              marginBottom: 40,
              maxWidth: 600,
            }}
          >
            Built on five convictions.
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <Principle
              number="01"
              title="Silence by default"
              body="Screens should feel quiet at first glance. Information density comes from structure, not saturation."
            />
            <Principle
              number="02"
              title="Dual typography roles"
              body="Serif carries meaning and context. Sans carries system state, labels, and controls."
            />
            <Principle
              number="03"
              title="Color as metadata"
              body="Accent colors tag health domains and status. Neutrals carry layout, surfaces, and interaction scaffolding."
            />
            <Principle
              number="04"
              title="Evidence with action"
              body="Every recommendation remains adjacent to the data and references that justify it."
            />
            <Principle
              number="05"
              title="Translucent navigation"
              body="Persistent floating navigation creates orientation without adding weight to the content plane."
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer
        className="px-6 sm:px-10 lg:px-16 py-10"
        style={{ borderTop: "1px solid rgba(108,108,112,0.10)" }}
      >
        <div className="flex flex-wrap items-center justify-between gap-4 max-w-6xl mx-auto">
          <p
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: "var(--color-kin-black)",
              fontFamily: "'Cormorant Garamond', Georgia, serif",
            }}
          >
            Kin Health
          </p>
          <p style={{ fontSize: 11, color: "var(--color-kin-muted)", letterSpacing: "0.10em" }}>
            MOBILE SCREEN DESIGN · PROPOSED · CONFIDENTIAL
          </p>
        </div>
      </footer>
    </div>
  );
}
